/**
 * Tag Service
 * Service for managing tag types and tags from backend
 */
import { sharedApiClient } from './client';

// ─── Tag Type Types ─────────────────────────────────────────────────────────────

export type TagSelectionMode = 'single' | 'multiple';

export interface TagType {
    id: string;
    tenant_id: string | null;
    is_master: boolean;
    is_system: boolean;
    code: string;
    name: string;
    status: 'active' | 'inactive';
    description: string;
    display_order: number;
    selection_mode: TagSelectionMode;
    is_required: boolean;
    is_inherited: boolean;
    created_at: string;
    updated_at: string;
}

export interface TagTypeListItem extends TagType {
    tag_count: number;
    /** Content Bank: true when this tag type is saved in toolbar filter config. */
    configured?: boolean;
}

export interface CreateTagTypeData {
    code: string;
    name: string;
    status?: 'active' | 'inactive';
    description?: string;
    display_order?: number;
    selection_mode?: TagSelectionMode;
    is_required?: boolean;
    is_inherited?: boolean;
    tenant_id?: string;
    /** Staff-only — creates a system catalogue TagType (backend rejects for tenant callers). */
    is_system?: boolean;
}

export interface UpdateTagTypeData {
    code?: string;
    name?: string;
    status?: 'active' | 'inactive';
    description?: string;
    display_order?: number;
    selection_mode?: TagSelectionMode;
    is_required?: boolean;
    is_inherited?: boolean;
    /** Staff + master row: `false` triggers backend demotion (clones per tenant). */
    is_master?: boolean;
}

/** Response shape when PUT demotes a master TagType to tenant-scoped clones. */
export interface TagTypeDemoteMasterResponse {
    demoted_master_tag_type_id: string;
    tenant_tag_types: TagType[];
}

export interface TagTypeListParams {
    tenant_id?: string;
    status?: string;
    search?: string;
    /** EAA-1769: optional server-side filter on TagType.is_required. */
    is_required?: boolean;
    /** EAA-1769: optional server-side filter on TagType.is_inherited. */
    is_inherited?: boolean;
    page?: number;
    page_size?: number;
    ordering?: string;
}

export interface TagTypeListResponse {
    results: TagTypeListItem[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

// ─── Tag Types ──────────────────────────────────────────────────────────────────

export interface Tag {
    id: string;
    tenant_id: string;
    tag_type_id: string;
    tag_type_name: string;
    code: string;
    name: string;
    status: 'active' | 'inactive';
    description: string;
    created_at: string;
    updated_at: string;
}

export interface CreateTagData {
    tag_type_id: string;
    code: string;
    name: string;
    status?: 'active' | 'inactive';
    description?: string;
    tenant_id?: string;
}

export interface UpdateTagData {
    tag_type_id?: string;
    code?: string;
    name?: string;
    status?: 'active' | 'inactive';
    description?: string;
}

export interface TagListParams {
    tenant_id?: string;
    /** EAA-1682: pass an array to fetch tags for several types in ONE request (joined as a comma-separated list) instead of one request per type. */
    tag_type_id?: string | string[];
    status?: string;
    search?: string;
    /** EAA-1769: optional server-side filter on Tag.tag_type.is_required. */
    is_required?: boolean;
    /** EAA-1769: optional server-side filter on Tag.tag_type.is_inherited. */
    is_inherited?: boolean;
    page?: number;
    page_size?: number;
    ordering?: string;
}

export interface TagListResponse {
    results: Tag[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

// ─── Service ────────────────────────────────────────────────────────────────────

class TagService {
    private basePath = '/tag';

    // ── Tag Type Methods ────────────────────────────────────────────────────

    async listTagTypes(params?: TagTypeListParams): Promise<TagTypeListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.tenant_id) queryParams.append('tenant_id', params.tenant_id);
        if (params?.status) queryParams.append('status', params.status);
        if (params?.search) queryParams.append('search', params.search);
        // EAA-1769: pass bool filters only when set so omit = no filter.
        if (params?.is_required !== undefined) {
            queryParams.append('is_required', String(params.is_required));
        }
        if (params?.is_inherited !== undefined) {
            queryParams.append('is_inherited', String(params.is_inherited));
        }
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));
        if (params?.ordering) queryParams.append('ordering', params.ordering);
        const qs = queryParams.toString();
        const url = qs
            ? `${this.basePath}/types/list/?${qs}`
            : `${this.basePath}/types/list/`;
        const response = await sharedApiClient.get<TagTypeListResponse>(url);
        return response.data;
    }

    async getTagType(id: string): Promise<TagType> {
        const response = await sharedApiClient.get<TagType>(`${this.basePath}/types/${id}`);
        return response.data;
    }

    async createTagType(data: CreateTagTypeData, isMaster = false): Promise<TagType> {
        const url = isMaster
            ? `${this.basePath}/types/?is_master=true`
            : `${this.basePath}/types/`;
        const response = await sharedApiClient.post<TagType>(url, data);
        return response.data;
    }

    async updateTagType(
        id: string,
        data: UpdateTagTypeData
    ): Promise<TagType | TagTypeDemoteMasterResponse> {
        const response = await sharedApiClient.put<TagType | TagTypeDemoteMasterResponse>(
            `${this.basePath}/types/${id}`,
            data
        );
        return response.data;
    }

    async deleteTagType(id: string): Promise<void> {
        await sharedApiClient.delete(`${this.basePath}/types/${id}`);
    }

    // ── Tag Methods ─────────────────────────────────────────────────────────

    async listTags(params?: TagListParams): Promise<TagListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.tenant_id) queryParams.append('tenant_id', params.tenant_id);
        if (params?.tag_type_id) {
            const tagTypeId = Array.isArray(params.tag_type_id)
                ? params.tag_type_id.join(',')
                : params.tag_type_id;
            if (tagTypeId) queryParams.append('tag_type_id', tagTypeId);
        }
        if (params?.status) queryParams.append('status', params.status);
        if (params?.search) queryParams.append('search', params.search);
        // EAA-1769: pass bool filters only when set so omit = no filter.
        if (params?.is_required !== undefined) {
            queryParams.append('is_required', String(params.is_required));
        }
        if (params?.is_inherited !== undefined) {
            queryParams.append('is_inherited', String(params.is_inherited));
        }
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));
        if (params?.ordering) queryParams.append('ordering', params.ordering);
        const qs = queryParams.toString();
        const url = qs
            ? `${this.basePath}/tags/list/?${qs}`
            : `${this.basePath}/tags/list/`;
        const response = await sharedApiClient.get<TagListResponse>(url);
        return response.data;
    }

    async getTag(id: string): Promise<Tag> {
        const response = await sharedApiClient.get<Tag>(`${this.basePath}/tags/${id}`);
        return response.data;
    }

    async createTag(data: CreateTagData): Promise<Tag> {
        const response = await sharedApiClient.post<Tag>(`${this.basePath}/tags/`, data);
        return response.data;
    }

    async updateTag(id: string, data: UpdateTagData): Promise<Tag> {
        const response = await sharedApiClient.put<Tag>(`${this.basePath}/tags/${id}`, data);
        return response.data;
    }

    async deleteTag(id: string): Promise<void> {
        await sharedApiClient.delete(`${this.basePath}/tags/${id}`);
    }
}

export const tagService = new TagService();
